import { Ref } from 'vue';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { AxiosError, AxiosResponse } from 'axios';

import { ApiError } from '@/types/commonApiType';

import { api } from './_http';

// etf 카테고리 리스트 조회
type GetEtfTabListParams = Ref<{
	category: string;
}>;
export type GetEtfTabListData = {
	etf_tab_list: string[];
};

export const useGeEtfTabListApi = (params: GetEtfTabListParams, options?: any) => {
	const url = '/v1/front/usa/getEtfTabList';
	return useQuery<AxiosResponse, AxiosError, GetEtfTabListData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => {
			return res.data?.data;
		},
		...options,
	});
};

// etf 리스트 조회
type GetEtfParams = Ref<{
	category: string;
	tab: string;
}>;
export type GetEtfData = {
	etf: Etf[];
	etf_count: number;
};
export type Etf = {
	etf_event_name: string;
	etf_event_ticker: string;
	etf_event_tab: string;
	etf_pk: number;
	etf_event_pk: number;
	bookmark: number;
	etf_event_color: string;
};
export const useGetEtfInfiniteApi = (params: GetEtfParams, options?: any) => {
	const url = '/v1/front/usa/getEtf';
	return useInfiniteQuery<{ data: GetEtfData }, ApiError, GetEtfData, [string, GetEtfParams]>({
		queryKey: [url, params],
		queryFn: async ({ pageParam = 1 }) => {
			const res = await api.get<AxiosResponse<GetEtfData>>(url, {
				category: params.value.category,
				tab: params.value.tab,
				page: pageParam,
			});
			return res.data;
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage: { data: GetEtfData }, allPages: { data: GetEtfData }[]) => {
			// Last Page Check
			const totalItem = lastPage.data.etf_count;
			const currentItem = allPages.length * 5;
			const isNext = totalItem > currentItem ? true : null;

			return isNext && allPages.length + 1;
		},
		select: (res) => {
			// alram page format
			const etf = res.pages.reduce<Etf[]>((prev, cur) => {
				return [...prev, ...cur.data.etf];
			}, []);

			return {
				etf_count: res.pages[0].data.etf_count ?? 0,
				etf: etf ?? [],
			};
		},
		staleTime: 0,
		placeholderData: keepPreviousData,
		...options,
	});
};

// etf 카테고리 리스트 조회
type GetEtfEventDetailParams = Ref<{
	etf_pk: string;
	etf_event_pk: string;
}>;
export type GetEtfEventDetailData = {
	etf_event_detail: EtfEventDetail[];
	etf_event_holding_proportion: EtfEventHoldingProportion[];
};

type EtfEventDetail = {
	etf_event_asset_size: number;
	etf_event_asset_unit: string;
	etf_event_base_date: string;
	etf_event_description: string;
	etf_event_enrollment_date: string;
	etf_event_line_description: string;
	etf_event_listing_date: string;
	etf_event_management_company: string;
	etf_event_name: string;
	etf_event_pk: number;
	etf_event_ticker: string;
	etf_event_total_compensation: number;
};

type EtfEventHoldingProportion = {
	etf_holding_name: string;
	etf_holding_proportion: number;
};

export const useGeEtfEventDetailApi = (params: GetEtfEventDetailParams, options?: any) => {
	const url = '/v1/front/usa/getEtfEventDetail';
	return useQuery<AxiosResponse, AxiosError, GetEtfEventDetailData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => {
			return res.data?.data;
		},
		...options,
	});
};

